import React, { useState, useEffect } from 'react';
import { Typography, Card, Breadcrumb, Button, Space, Spin, Tag,  } from 'antd';
import { showConfirmModal } from '../components/PopupModal';
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { EventItem, getExposureDisplayText } from '../types/event';
import eventService from '../services/events';
import EventPreviewModal from '../components/EventPreviewModal';
import { message } from '../utils/message';

const { Title, Text } = Typography;

const EventDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [eventDetail, setEventDetail] = useState<EventItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  // 이벤트 상세 정보 로드
  useEffect(() => {
    const loadEventDetail = async () => {
      if (!eventId) {
        message.error('잘못된 접근입니다.');
        navigate('/event-management');
        return;
      }

      try {
        setLoading(true);
        const data = await eventService.getEventDetail(Number(eventId));
        // console.log('이벤트 상세 데이터:', {
        //   id: data.id,
        //   startDate: data.startDate,
        //   endDate: data.endDate,
        //   startDateType: typeof data.startDate,
        //   endDateType: typeof data.endDate
        // });
        setEventDetail(data);
      } catch (error) {
        console.error('이벤트 상세 조회 실패:', error);
        message.error('이벤트 정보를 불러오는 중 오류가 발생했습니다.');
        navigate('/event-management');
      } finally {
        setLoading(false);
      }
    };

    loadEventDetail();
  }, [eventId, navigate]);

  // 삭제 핸들러
  const handleDelete = () => {
    if (!eventDetail) return;

    showConfirmModal({
      title: '알림',
      content: '삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?',
      okText: '확인',
      okType: 'danger',
      onConfirm: async () => {
        try {
          setDeleteLoading(true);
          await eventService.deleteEvent(eventDetail.id);
          message.success('삭제가 완료되었습니다.');
          navigate('/event-management');
        } catch (error) {
          console.error('이벤트 삭제 실패:', error);
          message.error('이벤트 삭제 중 오류가 발생했습니다.');
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  // 뒤로가기 핸들러
  const handleGoBack = () => {
    navigate('/event-management');
  };

  // 편집 핸들러
  const handleEdit = () => {
    navigate(`/event-management/edit/${eventId}`);
  };

  // 다운로드 핸들러
  const handleDownload = async () => {
    if (!eventDetail?.id) {
      message.error('다운로드할 파일이 없습니다.');
      return;
    }

    try {
      // eventService를 통해 인증된 요청으로 파일 다운로드
      // 파일명은 백엔드에서 원본 파일명(metadata.originalName)을 사용
      await eventService.downloadEvent(eventDetail.id);
      message.success('파일 다운로드가 시작되었습니다.');
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      message.error('파일 다운로드 중 오류가 발생했습니다.');
    }
  };

  // 미리보기 핸들러
  const handlePreview = () => {
    if (!eventDetail?.videoFile) {
      message.warning('미리보기할 비디오 파일이 없습니다.');
      return;
    }
    setPreviewVisible(true);
  };

  // 미리보기 모달 닫기 핸들러
  const handlePreviewClose = () => {
    setPreviewVisible(false);
  };

  if (loading) {
    return (
      <div style={{ padding: '0', textAlign: 'center', marginTop: '100px' }}>
        <Text style={{ color: '#ffffff' }}>로딩 중...</Text>
      </div>
    );
  }

  if (!eventDetail) {
    return (
      <div style={{ padding: '0', textAlign: 'center', marginTop: '100px' }}>
        <Text style={{ color: '#ffffff' }}>데이터를 찾을 수 없습니다.</Text>
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '미디어 아트' },
          { title: '이벤트 관리', href: '/event-management' },
          { title: '이벤트 상세' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0 0 32px 0',
      }}>
        <Title level={2} style={{ color: '#ffffff', margin: 0 }}>
          {eventDetail.mediaName}
        </Title>
        <Button onClick={handlePreview}>
          미리보기
        </Button>
      </div>

      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>기본 정보</Text>}
        style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              레이아웃명
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {eventDetail.layoutName || '-'}
            </Text>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              노출여부
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text>
                {getExposureDisplayText(eventDetail.exposureType)}
              </Text>
            </div>
          </div>

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              노출기간
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {(() => {
                const hasStartDate = eventDetail.startDate &&
                  eventDetail.startDate !== null &&
                  eventDetail.startDate !== 'null' &&
                  eventDetail.startDate !== '';
                const hasEndDate = eventDetail.endDate &&
                  eventDetail.endDate !== null &&
                  eventDetail.endDate !== 'null' &&
                  eventDetail.endDate !== '';

                // console.log('날짜 체크:', {
                //   hasStartDate,
                //   hasEndDate,
                //   startDate: eventDetail.startDate,
                //   endDate: eventDetail.endDate
                // });

                if (hasStartDate && hasEndDate) {
                  return `${new Date(eventDetail.startDate!).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }).replace(/\//g, '.')} ~ ${new Date(eventDetail.endDate!).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }).replace(/\//g, '.')}`;
                }
                return '상시노출';
              })()}
            </Text>
          </div>
        </div>
      </Card>

      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>이벤트 정보</Text>}
        style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              이벤트명
            </Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {eventDetail.mediaName}
            </Text>
          </div>

          {eventDetail.content && (
            <div>
              <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                내용
              </Text>
              <Text style={{ color: '#ffffff', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                {eventDetail.content}
              </Text>
            </div>
          )}

          <div>
            <Text style={{ color: '#ffffff', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              첨부파일
            </Text>
            {eventDetail.videoFile ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <Text style={{ color: '#ffffff', fontSize: '14px', margin: 0 }}>
                    {eventDetail.videoFile.split('/').pop() || '파일'}
                  </Text>
                  <Button
                    type="default"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    style={{
                      background: '#F24822',
                      border: '1px solid #F24822',
                      color: '#ffffff'
                    }}
                  >
                    저장
                  </Button>
                </div>
                <div style={{
                  width: '100%',
                  maxWidth: '600px',
                  height: '300px',
                  backgroundColor: '#000',
                  border: '1px solid #404040',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  {videoLoading && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2
                    }}>
                      <Spin size="large" />
                      <div style={{ color: '#a6a6a6', marginTop: '8px', fontSize: '14px' }}>
                        비디오 로딩 중...
                      </div>
                    </div>
                  )}

                  {videoError ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#a6a6a6',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '16px', marginBottom: '8px' }}>⚠️</div>
                      <div style={{ fontSize: '14px' }}>
                        비디오를 불러올 수 없습니다.
                      </div>
                      <Button
                        size="small"
                        style={{ marginTop: '8px' }}
                        onClick={() => {
                          setVideoError(false);
                          setVideoLoading(true);
                        }}
                      >
                        다시 시도
                      </Button>
                    </div>
                  ) : (
                    <video
                      controls
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '8px',
                        display: videoLoading ? 'none' : 'block'
                      }}
                      poster=""
                      onLoadStart={() => setVideoLoading(true)}
                      onCanPlay={() => setVideoLoading(false)}
                      onError={() => {
                        setVideoLoading(false);
                        setVideoError(true);
                      }}
                    >
                      <source
                        src={(eventDetail as any).videoUrl || `/uploads/videos/${eventDetail.videoFile}`}
                        type="video/mp4"
                      />
                      <Text style={{ color: '#a6a6a6' }}>
                        브라우저가 비디오 재생을 지원하지 않습니다.
                      </Text>
                    </video>
                  )}
                </div>
                <Text style={{ color: '#a6a6a6', fontSize: '11px', marginTop: '8px', display: 'block' }}>
                  {eventDetail.fileSize && (
                    <span style={{ marginLeft: '12px' }}>
                      총 1개 ({(eventDetail.fileSize / (1024 * 1024)).toFixed(2)}MB)
                    </span>
                  )}
                </Text>
              </div>
            ) : (
              <Text style={{ color: '#ffffff', fontSize: '14px' }}>-</Text>
            )}
          </div>
        </div>
      </Card>

      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>등록 정보</Text>}
        style={{ marginBottom: '24px', background: '#1f1f1f', border: '1px solid #303030' }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, minmax(0, 1fr))', gap: '20px' }}>
          <div>
            <Text style={{fontWeight: 'bold', color: '#a6a6a6', fontSize: '13px', display: 'block', marginBottom: '4px' }}>등록일</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {new Date(eventDetail.createdAt).toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }).replace(/\//g, '.')}
            </Text>
          </div>
          <div>
            <Text style={{fontWeight: 'bold', color: '#a6a6a6', fontSize: '13px', display: 'block', marginBottom: '4px' }}>최근 수정일</Text>
            <Text style={{ color: '#ffffff', fontSize: '14px' }}>
              {eventDetail.updatedAt ?
                new Date(eventDetail.updatedAt).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }).replace(/\//g, '.') : '-'
              }
            </Text>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px' }}>
        <Button
          danger
          size="large"
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          loading={deleteLoading}
          style={{ minWidth: '100px' }}
        >
          삭제
        </Button>

        <Button
          type="primary"
          size="large"
          onClick={handleEdit}
          style={{ minWidth: '100px', background: '#F24822', border: 'none' }}
        >
          수정
        </Button>
      </div>

      {/* 미리보기 모달 */}
      <EventPreviewModal
        visible={previewVisible}
        onClose={handlePreviewClose}
        event={eventDetail}
      />
    </Space>
  );
};

export default EventDetail;
